import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { HelpCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";

interface HelpIconProps {
  tooltipMessage?: string | ReactNode;
  tooltipHtml?: ReactNode;
  defaultOpen?: boolean;
}

export function HelpIcon({ tooltipMessage, tooltipHtml, defaultOpen }: HelpIconProps) {
  const isMobile = useIsMobile();

  // --- VERSÃO MOBILE (POPOVER) ---
  if (isMobile) {
    return (
      <Popover defaultOpen={defaultOpen}>
        <PopoverTrigger>
          <span
            tabIndex={0}
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <HelpCircle className="h-4 w-4 cursor-pointer text-muted-foreground transition-colors hover:text-foreground" />
          </span>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          className={cn(
            "w-[280px] max-w-xs whitespace-pre-wrap break-words",
            "rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md",
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out",
            "z-50",
          )}
        >
          <PopoverArrow className="fill-popover" />
          <div className="text-xs leading-relaxed">{tooltipHtml || tooltipMessage}</div>
        </PopoverContent>
      </Popover>
    );
  }

  // --- VERSÃO DESKTOP (TOOLTIP) ---
  return (
    <TooltipProvider>
      <Tooltip defaultOpen={defaultOpen}>
        <TooltipTrigger>
          <span
            tabIndex={0}
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <HelpCircle className="h-4 w-4 cursor-help text-muted-foreground transition-colors hover:text-foreground" />
          </span>
        </TooltipTrigger>
        {/* Mesmo teto do popover mobile: texto de ajuda é frase longa, sem max vira uma faixa na tela. */}
        <TooltipContent
          side="top"
          className="max-w-[280px] whitespace-normal break-words rounded leading-relaxed"
        >
          {tooltipHtml ? (
            <span>{tooltipHtml}</span>
          ) : (
            <div className="text-xs">{tooltipMessage}</div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
